import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { openTawktrikChat } from '../utils/chatUtils';

const ServicesDropdown = ({ isScrolled }) => {
  const [activeCategory, setActiveCategory] = useState('trademark');
  
  const serviceCategories = {
    trademark: {
      title: 'Trademark Services',
      description: 'Comprehensive trademark protection for your brand',
      services: [
        { name: 'Trademark Search & Registration', link: '/services/trademark/search-registration' },
        { name: 'Trademark Clearance Searches', link: '/services/trademark/clearance' },
        { name: 'Trademark Application Preparation', link: '/services/trademark/application' },
        { name: 'Trademark Office Actions', link: '/services/trademark/office-actions' },
        { name: 'Trademark Monitoring', link: '/services/trademark/monitoring' },
        { name: 'Statement Of Use Filings', link: '/services/trademark/statement-of-use' },
        { name: 'Trademark Change of Ownership', link: '/services/trademark/ownership-change' },
        { name: 'Trademark Portfolio Management', link: '/services/trademark/portfolio' }
      ]
    },
    copyright: {
      title: 'Copyright Services',
      description: 'Protect your creative works and intellectual content',
      services: [
        { name: 'Copyright Registration', link: '/services/copyright/registration' },
        { name: 'Copyright Enforcement', link: '/services/copyright/enforcement' },
        { name: 'Copyright Assignments and Licensing', link: '/services/copyright/assignments' },
        { name: 'Copyright Litigation', link: '/services/copyright/litigation' },
        { name: 'Copyright Portfolio Management', link: '/services/copyright/portfolio' },
        { name: 'Digital Millennium Copyright Act', link: '/services/copyright/dmca' }
      ]
    },
    patent: {
      title: 'Patent Services',
      description: 'Secure your innovations and inventions',
      services: [
        { name: 'Patent Searches', link: '/services/patent/searches' },
        { name: 'Office Actions & Amendments', link: '/services/patent/office-actions' },
        { name: 'Patent Applications', link: '/services/patent/applications' },
        { name: 'Patent Enforcement', link: '/services/patent/enforcement' }
      ]
    },
    company: {
      title: 'Company Formation',
      description: 'Establish your business structure properly',
      services: [
        { name: 'LLC Formation', link: '/services/company/llc' },
        { name: 'C Corporation', link: '/services/company/c-corp' },
        { name: 'S Corporation', link: '/services/company/s-corp' },
        { name: 'Non-Profit Organization', link: '/services/company/non-profit' }
      ]
    }
  };

  return (
    <div className="relative group">
      <button className="flex items-center text-[#F1F1F1] hover:text-[#FFB400] transition-colors">
        Services
        <ChevronDown className="ml-1 h-4 w-4" />
      </button>
      <div className={`fixed left-0 right-0 bg-white shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 overflow-hidden z-50 ${isScrolled ? 'top-16' : 'top-24'}`}>
        <div className="max-w-7xl mx-auto flex">
          {/* Left Sidebar - Categories */}
          <div className="w-72 bg-[#2B0A12] p-8">
            <h3 className="text-[#FFB400] font-bold text-sm uppercase mb-6">Our Services</h3>
            <div className="space-y-2">
              {Object.entries(serviceCategories).map(([key, category]) => (
                <button
                  key={key}
                  onMouseEnter={() => setActiveCategory(key)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center justify-between ${
                    activeCategory === key 
                      ? 'bg-[#FFB400] text-[#2B0A12] font-semibold' 
                      : 'text-[#F1F1F1] hover:bg-[#3D1C24]'
                  }`}
                >
                  <span className="text-sm">{category.title}</span>
                  {activeCategory === key && <ChevronRight className="h-4 w-4" />}
                </button>
              ))}
            </div>
          </div>

          {/* Right Content - Services List */}
          <div className="flex-1 p-10 bg-gray-50">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-[#2B0A12] mb-3">
                {serviceCategories[activeCategory].title}
              </h2>
              <p className="text-gray-600 text-lg">
                {serviceCategories[activeCategory].description}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {serviceCategories[activeCategory].services.map((service, index) => (
                <Link
                  key={index}
                  to={service.link}
                  className="flex items-start space-x-3 p-4 rounded-lg hover:bg-white hover:shadow-md transition-all group/item"
                >
                  <ChevronRight className="h-5 w-5 text-[#D72638] flex-shrink-0 mt-0.5 group-hover/item:translate-x-1 transition-transform" />
                  <span className="text-sm text-gray-700 group-hover/item:text-[#D72638] font-medium">
                    {service.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll to show/hide top strip
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu and dropdowns when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar Strip */}
      <div className={`bg-[#FFB400] transition-all duration-300 ${isScrolled ? 'hidden' : 'block'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end items-center py-2">
            <div className="flex items-center space-x-6 text-[#2B0A12] text-sm">
              <a href="tel:+18882031297" className="flex items-center space-x-2 hover:text-[#2B0A12]/80 transition-colors">
                <Phone className="h-4 w-4" />
                <span className="hidden sm:inline">+1 (888) 203-1297</span>
              </a>
              <a href="mailto:info@trademarkessential.com" className="flex items-center space-x-2 hover:text-[#2B0A12]/80 transition-colors max-[664px]:hidden">
                <Mail className="h-4 w-4" />
                <span>info@trademarkessential.com</span>
              </a>
              <div className="flex items-center space-x-2 max-[991px]:hidden">
                <MapPin className="h-4 w-4" />
                <span>7060 Hollywood Blvd
Los Angeles, CA 90028</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className={`fixed left-0 right-0 z-50 bg-[#2B0A12] shadow-lg transition-all duration-300 ${isScrolled ? 'top-0' : 'top-8'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/assets/img/brand/logo-light.png" 
              alt="Trademark Essential Logo" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-[#F1F1F1] hover:text-[#FFB400] transition-colors ${
                isActive('/') ? 'text-[#FFB400]' : ''
              }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`text-[#F1F1F1] hover:text-[#FFB400] transition-colors ${
                isActive('/about') ? 'text-[#FFB400]' : ''
              }`}
            >
              About Us
            </Link>
            <ServicesDropdown isScrolled={isScrolled} />
            <Link
              to="/resources"
              className={`text-[#F1F1F1] hover:text-[#FFB400] transition-colors ${
                isActive('/resources') ? 'text-[#FFB400]' : ''
              }`}
            >
              Resources
            </Link>
            <Link
              to="/contact"
              className={`text-[#F1F1F1] hover:text-[#FFB400] transition-colors ${
                isActive('/contact') ? 'text-[#FFB400]' : ''
              }`}
            >
              Contact
            </Link>
            <Button 
              onClick={openTawktrikChat}
              className="bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-semibold"
            >
              Free Consultation
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-[#F1F1F1]"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden pb-4">
            <div className="flex flex-col space-y-4">
              <Link to="/" className="text-[#F1F1F1] hover:text-[#FFB400] transition-colors">
                Home
              </Link>
              <Link to="/about" className="text-[#F1F1F1] hover:text-[#FFB400] transition-colors">
                About Us
              </Link>
              <div>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="flex items-center text-[#F1F1F1] hover:text-[#FFB400] transition-colors w-full"
                >
                  Services
                  <ChevronDown className={`ml-1 h-4 w-4 transform transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isServicesOpen && (
                  <div className="ml-4 mt-2 flex flex-col space-y-2">
                    <Link to="/services/trademark" className="text-[#F1F1F1] hover:text-[#FFB400] transition-colors">
                      Trademark Registration
                    </Link>
                    <Link to="/services/copyright" className="text-[#F1F1F1] hover:text-[#FFB400] transition-colors">
                      Copyright Registration
                    </Link>
                    <Link to="/services/patent" className="text-[#F1F1F1] hover:text-[#FFB400] transition-colors">
                      Patent Services
                    </Link>
                    <Link to="/services/company-formation" className="text-[#F1F1F1] hover:text-[#FFB400] transition-colors">
                      Company Formation
                    </Link>
                  </div>
                )}
              </div>
              <Link to="/resources" className="text-[#F1F1F1] hover:text-[#FFB400] transition-colors">
                Resources
              </Link>
              <Link to="/contact" className="text-[#F1F1F1] hover:text-[#FFB400] transition-colors">
                Contact
              </Link>
              <Button 
                onClick={openTawktrikChat}
                className="bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-semibold w-full"
              >
                Free Consultation
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};

export default Navbar;